import { gql, useQuery } from '@apollo/client';

import {
  useGroupPermissions_currentUser,
  useGroupPermissions_currentUserVariables,
} from './graphql-types/useGroupPermissions_currentUser';

enum EGroupPermission {
  ReadMemberDirectory = 'READ_MEMBER_DIRECTORY',
  UpdatePost = 'UPDATE_POST',
  UpdateComment = 'UPDATE_COMMENT',
  UpdateGroup = 'UPDATE_GROUP',
  ViewAdminPage = 'VIEW_ADMIN_PAGE',
  ViewSettingsPage = 'VIEW_SETTINGS_PAGE',

  UpdateConferenceCall = 'UPDATE_CONFRENCE_CALL',
  DeleteConferenceCall = 'DELETE_CONFERENCE_CALL',

  // Content
  DeletePost = 'DELETE_POST',
  PinPost = 'PIN_POST',
  EditPost = 'EDIT_POST',
  DeleteComment = 'DELETE_COMMENT',
  EditComment = 'EDIT_COMMENT',
  MakeAnnouncement = 'MAKE_ANNOUNCEMENT',

  // Settings
  UpdateSettings = 'UPDATE_SETTINGS',
  EditBannerImage = 'EDIT_BANNER_IMAGE',
  EditDescriptionAndDisplayName = 'EDIT_DESCRIPTION_AND_DISPLAY_NAME',
  EditMembership = 'EDIT_MEMBERSHIP',
  EditMemberModeratorRole = 'EDIT_MEMBER_MODERATOR_ROLE',

  // only a general admin can promote users to GroupAdmin
  EditMemberRoles = 'EDIT_MEMBER_ROLES',

  // Library
  EditLibrary = 'EDIT_LIBRARY',

  //Simple Event
  EditEvent = 'EDIT_EVENT',
}

const CURRENT_USER_QUERY = gql`
  query useGroupPermissions_currentUser($groupId: String!) {
    currentUser {
      _id
      groupPermissions(groupId: $groupId)
    }
  }
`;

export const useGroupPermissions = (
  groupId: string
): {
  isLoading: boolean;
  canReadMemberDirectory: boolean;
  canUpdatePost: boolean;
  canUpdateComment: boolean;
  canUpdateGroup: boolean;
  canViewAdminPage: boolean;
  canViewSettingsPage: boolean;
  canUpdateConferenceCall: boolean;
  canDeleteConferenceCall: boolean;
  canDeletePost: boolean;
  canPinPost: boolean;
  canEditPost: boolean;
  canDeleteComment: boolean;
  canEditComment: boolean;
  canMakeAnnouncement: boolean;
  canUpdateSettings: boolean;
  canEditBannerImage: boolean;
  canEditDescriptionAndDisplayName: boolean;
  canEditMembership: boolean;
  canEditMemberModeratorRole: boolean;
  canEditMemberRoles: boolean;
  canEditLibrary: boolean;
  canEditEvent: boolean;
} => {
  const query = useQuery<
    useGroupPermissions_currentUser,
    useGroupPermissions_currentUserVariables
  >(CURRENT_USER_QUERY, { variables: { groupId }, skip: !groupId });

  const groupPermissions = query.data?.currentUser?.groupPermissions || [];

  return {
    isLoading: query.loading,
    canReadMemberDirectory: groupPermissions.includes(
      EGroupPermission.ReadMemberDirectory
    ),
    canUpdatePost: groupPermissions.includes(EGroupPermission.UpdatePost),
    canUpdateComment: groupPermissions.includes(EGroupPermission.UpdateComment),
    canUpdateGroup: groupPermissions.includes(EGroupPermission.UpdateGroup),
    canViewAdminPage: groupPermissions.includes(EGroupPermission.ViewAdminPage),
    canViewSettingsPage: groupPermissions.includes(
      EGroupPermission.ViewSettingsPage
    ),
    canUpdateConferenceCall: groupPermissions.includes(
      EGroupPermission.UpdateConferenceCall
    ),
    canDeleteConferenceCall: groupPermissions.includes(
      EGroupPermission.DeleteConferenceCall
    ),
    canDeletePost: groupPermissions.includes(EGroupPermission.DeletePost),
    canPinPost: groupPermissions.includes(EGroupPermission.PinPost),
    canEditPost: groupPermissions.includes(EGroupPermission.EditPost),
    canDeleteComment: groupPermissions.includes(EGroupPermission.DeleteComment),
    canEditComment: groupPermissions.includes(EGroupPermission.EditComment),
    canMakeAnnouncement: groupPermissions.includes(
      EGroupPermission.MakeAnnouncement
    ),
    canUpdateSettings: groupPermissions.includes(
      EGroupPermission.UpdateSettings
    ),
    canEditBannerImage: groupPermissions.includes(
      EGroupPermission.EditBannerImage
    ),
    canEditDescriptionAndDisplayName: groupPermissions.includes(
      EGroupPermission.EditDescriptionAndDisplayName
    ),
    canEditMembership: groupPermissions.includes(
      EGroupPermission.EditMembership
    ),
    canEditMemberModeratorRole: groupPermissions.includes(
      EGroupPermission.EditMemberModeratorRole
    ),
    canEditMemberRoles: groupPermissions.includes(
      EGroupPermission.EditMemberRoles
    ),
    canEditLibrary: groupPermissions.includes(EGroupPermission.EditLibrary),
    canEditEvent: groupPermissions.includes(EGroupPermission.EditEvent),
  };
};
